import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Envelope, Lock, GoogleLogo, ArrowRight, ShieldCheck, ArrowLeft } from '@phosphor-icons/react';

// Logo component
const FinanciALLogo = () => (
  <img 
    src="https://customer-assets-0z36b82j.emergentagent.net/job_1ce9899f-22e9-4951-9d4b-a91c40afd391/artifacts/ktwxigwh_Liquid%20Glass%20Logo.png.png" 
    alt="FinanciAL" 
    className="w-10 h-10 rounded-xl object-cover"
  />
);

const Login = () => {
  const navigate = useNavigate();
  const { login, loginWithGoogle } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  // 2FA State
  const [show2FA, setShow2FA] = useState(false);
  const [twoFASessionId, setTwoFASessionId] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [verifying2FA, setVerifying2FA] = useState(false);
  const [resending, setResending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await authAPI.login(formData);
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        setTwoFASessionId(response.data.session_id);
        setShow2FA(true);
        toast.info(response.data.message || 'Kodi u dërgua në email tuaj');
        setLoading(false);
        return;
      }
      
      // Normal login (no 2FA)
      localStorage.setItem('auth_token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      toast.success('Mirë se erdhët!');
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kredencialet e gabuara');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!otpCode.trim()) {
      toast.error('Ju lutem vendosni kodin');
      return;
    }
    
    setVerifying2FA(true);
    try {
      const response = await authAPI.verify2FA(otpCode.trim(), twoFASessionId);
      
      localStorage.setItem('auth_token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      toast.success('Mirë se erdhët!');
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kodi është i pasaktë');
    } finally {
      setVerifying2FA(false);
    }
  };

  const handleResend2FA = async () => {
    setResending(true);
    try {
      await authAPI.resend2FACode(twoFASessionId);
      toast.success('Kodi u ridërgua me sukses');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dështoi ridërgimi i kodit');
    } finally {
      setResending(false);
    }
  };

  const handleBack = () => {
    setShow2FA(false);
    setOtpCode('');
    setTwoFASessionId(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F9FA] via-[#E9ECEF] to-[#DEE2E6] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <FinanciALLogo />
            <span className="text-2xl font-bold text-[#1A3C34]">FinanciAL</span>
          </Link>
          <h1 className="text-2xl font-bold text-[#1A3C34] mb-2">
            {show2FA ? 'Verifikimi 2FA' : 'Mirë se Erdhët Përsëri'}
          </h1>
          <p className="text-[#1A3C34]/60">
            {show2FA ? 'Vendosni kodin e dërguar në email tuaj' : 'Hyni në llogarinë tuaj për të vazhduar'}
          </p>
        </div>

        {/* Login/2FA Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-[#1A3C34]/5">
          <AnimatePresence mode="wait">
            {show2FA ? (
              <motion.div
                key="2fa"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <form onSubmit={handleVerify2FA} className="space-y-5">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 rounded-full bg-[#2A9D8F]/10 flex items-center justify-center mx-auto mb-4">
                      <ShieldCheck className="w-8 h-8 text-[#2A9D8F]" weight="fill" />
                    </div>
                    <p className="text-sm text-[#1A3C34]/60">
                      Kemi dërguar një kod 6-shifror në <strong>{formData.email}</strong>
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="otp" className="text-[#1A3C34]">Kodi i Verifikimit</Label>
                    <Input
                      id="otp"
                      type="text"
                      placeholder="000000"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="text-center text-2xl tracking-[0.5em] font-mono border-[#1A3C34]/10 focus:border-[#2A9D8F] focus:ring-[#2A9D8F]"
                      maxLength={6}
                      autoFocus
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#1A3C34] hover:bg-[#1A3C34]/90 text-white py-6"
                    disabled={verifying2FA || otpCode.length !== 6}
                  >
                    {verifying2FA ? 'Duke verifikuar...' : 'Verifiko'}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                  
                  <div className="flex items-center justify-between pt-2">
                    <button
                      type="button"
                      onClick={handleBack}
                      className="text-sm text-[#1A3C34]/60 hover:text-[#1A3C34] flex items-center gap-1"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Kthehu
                    </button>
                    <button
                      type="button"
                      onClick={handleResend2FA}
                      disabled={resending}
                      className="text-sm text-[#2A9D8F] hover:underline disabled:opacity-50"
                    >
                      {resending ? 'Duke dërguar...' : 'Ridërgo kodin'}
                    </button>
                  </div>
                </form>
              </motion.div>
            ) : (
              <motion.div
                key="login"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
              >
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#1A3C34]">Email</Label>
                    <div className="relative">
                      <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1A3C34]/40" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="emri@shembull.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="pl-10 border-[#1A3C34]/10 focus:border-[#2A9D8F] focus:ring-[#2A9D8F]"
                        data-testid="login-email-input"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <Label htmlFor="password" className="text-[#1A3C34]">Fjalëkalimi</Label>
                      <Link to="/forgot-password" className="text-sm text-[#2A9D8F] hover:underline">
                        Keni harruar?
                      </Link>
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1A3C34]/40" />
                      <Input
                        id="password"
                        type="password"
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="pl-10 border-[#1A3C34]/10 focus:border-[#2A9D8F] focus:ring-[#2A9D8F]"
                        data-testid="login-password-input"
                        required
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#1A3C34] hover:bg-[#1A3C34]/90 text-white py-6"
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? 'Duke hyrë...' : 'Hyr'}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </form>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-[#1A3C34]/10"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-[#1A3C34]/50">ose</span>
                  </div>
                </div>

                <Button
                  variant="outline"
                  className="w-full border-[#1A3C34]/10 hover:bg-[#F8F9FA] py-6"
                  onClick={loginWithGoogle}
                >
                  <GoogleLogo className="w-5 h-5 mr-2" weight="bold" />
                  Vazhdo me Google
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Sign up link */}
        {!show2FA && (
          <p className="text-center mt-6 text-[#1A3C34]/60">
            Nuk keni llogari?{' '}
            <Link to="/register" className="text-[#2A9D8F] font-medium hover:underline">
              Regjistrohu
            </Link>
          </p>
        )}
      </motion.div>
    </div>
  );
};

export default Login;
